/**
 * Realtime Bridge Provider - 简单业务代理
 *
 * 职责：
 * 1. 为非组件环境提供全局访问 Auth Provider 的接口
 * 2. 简单代理 Auth Provider 的方法，不包含业务逻辑
 * 3. 为实时事件处理器提供统一的调用入口
 * 4. 提供基础的 Toast 显示功能
 *
 * 设计理念：
 * - 不包含任何业务逻辑，只做简单的方法转发
 * - 所有复杂逻辑都委托给 Auth Provider 处理
 * - 作为 Auth Provider 和事件处理器之间的桥梁
 */

"use client"

import { atom, useAtom, useSetAtom } from "jotai"
import type React from "react"
import { useEffect } from "react"
import { toast } from "@/components/pop-ui/pop-toast"
import { getAuthMethods } from "./auth-provider"

// 定义全局方法的类型
interface RealtimeBridgeMethods {
	handleUserLogout: (options: {
		redirectUrl?: string
		clearQueryCache?: boolean
		showToast?: boolean
		toastMessage?: string
	}) => Promise<void>
	showToast: (
		message: string,
		type?: "success" | "error" | "warning" | "info",
		duration?: number,
	) => void
	reloadPage: () => void
}

// 创建全局方法的 atom
export const realtimeBridgeMethodsAtom = atom<RealtimeBridgeMethods | null>(
	null,
)

// 全局访问器，用于在非组件环境中访问方法
let globalBridgeMethods: RealtimeBridgeMethods | null = null

// 导出给非组件环境使用的方法
export const getRealtimeBridgeMethods = (): RealtimeBridgeMethods | null => {
	return globalBridgeMethods
}

interface RealtimeBridgeProviderProps {
	children: React.ReactNode
}

export function RealtimeBridgeProvider({
	children,
}: RealtimeBridgeProviderProps) {
	const setMethods = useSetAtom(realtimeBridgeMethodsAtom)

	useEffect(() => {
		// 创建方法对象，完全依赖 Auth Provider
		const methods: RealtimeBridgeMethods = {
			handleUserLogout: async (options) => {
				try {
					const authMethods = getAuthMethods()
					if (authMethods) {
						await authMethods.logout({
							redirectUrl: options.redirectUrl || "/",
							clearQueryCache: options.clearQueryCache ?? true,
							showToast: options.showToast ?? true,
							toastMessage: options.toastMessage,
						})
					} else {
						console.error("Auth methods not available for logout")
						// 最后备用：直接重载页面
						setTimeout(() => {
							window.location.reload()
						}, 100)
					}
				} catch (error) {
					console.error("Error in bridge handleUserLogout:", error)
					// 备用处理：直接跳转
					window.location.reload()
				}
			},

			showToast: (message: string, type = "info", duration = 5000) => {
				try {
					const toastFn = getToastFunction(type)
					toastFn(message, { duration })
				} catch (error) {
					console.error("Error in bridge showToast:", error)
					// 备用处理：使用默认 toast
					toast.info(message, { duration })
				}
			},

			reloadPage: () => {
				try {
					window.location.reload()
				} catch (error) {
					console.error("Error in bridge reloadPage:", error)
				}
			},
		}

		// 设置到 atom 中
		setMethods(methods)

		// 设置全局访问器
		globalBridgeMethods = methods

		// 清理函数
		return () => {
			globalBridgeMethods = null
			setMethods(null)
		}
	}, [setMethods])

	return <>{children}</>
}

// 获取适当的 toast 函数
function getToastFunction(type: "success" | "error" | "warning" | "info") {
	switch (type) {
		case "success":
			return toast.success
		case "warning":
			return toast.warning
		case "error":
			return toast.error
		default:
			return toast.info
	}
}

// Hook 用于在组件中访问方法
export function useRealtimeBridge() {
	const [methods] = useAtom(realtimeBridgeMethodsAtom)
	return methods
}
